// src/pages/inventory/commerce/tabs/SalesOrdersTab.tsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button, Space, Input, Tooltip, Typography, DatePicker } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import { fetchSalesOrders, SalesOrderLabelLookup } from "../../../../../api/inventory/salesOrderApi";
import type { SalesOrder } from "../../../../../types/inventory/OrderTypes";
import EditSalesOrderDrawer from "../drawers/EditSalesOrderDrawer";
import { useNotifications } from "../../../../../context/NotificationContext";
import { loadUserPreferences } from "../../../../../utils/userPrefs/UserPrefs";
import { PageToolbar, DataTable, StatusTag } from "../../../../../components/shared";
import PatientSelect from "../../../../../components/finance/invoicing/PatientSelect";
import { patientLabel } from "../../../../../components/finance/invoicing/patientSearch";
import { useOpenPatientModal } from "../../../../../hooks/xoe-actions/useOpenPatientModal";
import { canViewPatients } from "../../../../../utils/userPrefs/validatePermissions";
import { useSalesOrderAssignees } from "./useSalesOrderAssignees";
import { debounce } from "lodash";
import dayjs from 'dayjs';

const { Text, Link } = Typography;

const defaultSalesOrder: SalesOrder = {
	assignee_type: "user",
	assignee_id: "",
	order_date: new Date().toISOString(),
	status: "pending",
	notes: "",
	items: [],
};

// A stable key for the ACTIVE column filters only. antd reports cleared columns as
// null and builds a fresh object on every table change, so neither identity nor a
// raw JSON compare can tell "the user filtered something" from "the user clicked a
// page". Ignoring null/empty entries and sorting the keys makes the comparison both
// order-independent and blind to the empty-vs-all-null difference between our
// initial {} and antd's first payload.
const activeFilterKey = (filters: Record<string, any>): string =>
	JSON.stringify(
		Object.keys(filters || {})
			.filter((k) => filters[k] && filters[k].length > 0)
			.sort()
			.map((k) => [k, filters[k]])
	);

const SalesOrdersTab: React.FC = () => {
	const [orders, setOrders] = useState<SalesOrder[]>([]);
	const [totalOrders, setTotalOrders] = useState<number>(0);
	const [selectedOrder, setSelectedOrder] = useState<SalesOrder | null>(null);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(false);
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
	const [globalSearchInput, setGlobalSearchInput] = useState<string>("");
	const [globalSearch, setGlobalSearch] = useState<string>("");
	const [sortField, setSortField] = useState<string>("sales_order_id");
	const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
	const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
	const [patientFilter, setPatientFilter] = useState<string | undefined>(undefined);
	const [labelLookup, setLabelLookup] = useState<SalesOrderLabelLookup | undefined>(undefined);
	const searchInput = useRef<any>(null);
	// Only the newest request may write to state. A search made from page 3 briefly
	// has two requests in flight — the stale page-3 one and the reset page-1 one —
	// and the page-3 response is an empty page, which would read as "kit not found".
	const requestSeq = useRef<number>(0);
	const { showError } = useNotifications();
	const { assignees, isLoading: assigneesLoading } = useSalesOrderAssignees(orders);
	const { openPatient, modals } = useOpenPatientModal();
	const ORDERS_PER_PAGE = loadUserPreferences().tableRowsPerPage;

	// Debounce only the value that drives the query — a barcode scanner types a whole
	// Label ID as a burst, and the label branch is the heaviest on the endpoint.
	const debouncedSetGlobalSearch = useMemo(
		() => debounce((v: string) => setGlobalSearch(v), 300),
		[]
	);

	useEffect(() => () => debouncedSetGlobalSearch.cancel(), [debouncedSetGlobalSearch]);

	useEffect(() => {
		loadOrders();
	}, [currentPage, globalSearch, sortField, sortDirection, columnFilters, patientFilter]);

	// Searching from page 3 would otherwise request page 3 of a 1-row result set
	useEffect(() => {
		setCurrentPage(1);
	}, [globalSearch, columnFilters, sortField, sortDirection, patientFilter]);

	const loadOrders = async () => {
		const seq = ++requestSeq.current;
		setLoading(true);
		try {
			const params: any = {
				page: currentPage,
				per_page: ORDERS_PER_PAGE,
				sort_field: sortField,
				sort_direction: sortDirection,
			};

			// Add global search parameter if provided
			if (globalSearch.trim()) {
				params.search = globalSearch.trim();
			}

			if (patientFilter) {
				params.assignee_type = "user";
				params.assignee_id = patientFilter;
			}

			// Add column filters
			Object.keys(columnFilters).forEach(key => {
				if (columnFilters[key] && columnFilters[key].length > 0) {
					if (key === 'status') {
						params.status = columnFilters[key][0];
					} else if (key === 'sales_order_id') {
						const raw = String(columnFilters[key][0] ?? '').trim();
						// Backend validates order_id as `integer` — forwarding free text would 422.
						if (/^\d+$/.test(raw)) { params.order_id = Number(raw); }
					} else if (key === 'customer_name') {
						params.customer_name = columnFilters[key][0];
					} else if (key === 'customer_email') {
						params.customer_email = columnFilters[key][0];
					} else if (key === 'is_shopify_order') {
						params.is_shopify_order = columnFilters[key][0] === 'true';
					} else if (key === 'order_date') {
						if (columnFilters[key].length === 2) {
							params.date_from = columnFilters[key][0];
							params.date_to = columnFilters[key][1];
						}
					}
				}
			});

			const { data, total, labelLookup: lookup } = await fetchSalesOrders(params);
			if (seq !== requestSeq.current) return;
			setOrders(data);
			setTotalOrders(total);
			// Assign unconditionally: the key is absent whenever it does not apply, so
			// this is also what clears a previous search's diagnostic.
			setLabelLookup(lookup);
		} catch (error) {
			if (seq !== requestSeq.current) return;
			console.error("Error loading sales orders:", error);
			// A stale diagnostic sitting under a failed request is worse than none.
			setLabelLookup(undefined);
			showError("Failed to load sales orders");
		} finally {
			if (seq === requestSeq.current) {
				setLoading(false);
			}
		}
	};

	const handleNewOrder = () => {
		setSelectedOrder(defaultSalesOrder);
		setDrawerOpen(true);
	};

	const handleEditOrder = (order: SalesOrder) => {
		setSelectedOrder(order);
		setDrawerOpen(true);
	};

	const handleDrawerClose = () => {
		setSelectedOrder(null);
		setDrawerOpen(false);
		loadOrders();
	};

	const clearAllFilters = () => {
		debouncedSetGlobalSearch.cancel();
		setGlobalSearchInput("");
		setGlobalSearch("");
		setColumnFilters({});
		setPatientFilter(undefined);
		setCurrentPage(1);
	};

	const getColumnSearchProps = (dataIndex: string, placeholder: string = `Search ${dataIndex}`) => ({
		filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
			<div style={{ padding: 8 }}>
				<Input
					ref={searchInput}
					placeholder={placeholder}
					value={selectedKeys[0]}
					onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
					onPressEnter={() => confirm()}
					style={{ marginBottom: 8, display: 'block' }}
				/>
				<Space>
					<Button
						type="primary"
						onClick={() => confirm()}
						icon={<SearchOutlined />}
						size="small"
					>
						Search
					</Button>
					<Button onClick={() => clearFilters()} size="small">
						Reset
					</Button>
				</Space>
			</div>
		),
		filterIcon: (filtered: boolean) => (
			<SearchOutlined style={{ color: filtered ? 'var(--mg-primary)' : undefined }} />
		),
	});

	const handleTableChange = (pagination: any, filters: any, sorter: any) => {
		if (sorter.field && sorter.order) {
			setSortField(sorter.field);
			setSortDirection(sorter.order === "ascend" ? "asc" : "desc");
		}
		// Update column filters. antd fires onChange for EVERY table change — including
		// a plain page click — and hands us a freshly-allocated filters object each
		// time. Assigning it unconditionally changes columnFilters' identity, which
		// re-triggers the page-reset effect and bounces the pager back to page 1,
		// making pagination inert. Keep the previous object when no filter actually
		// changed, so React bails out of the update.
		setColumnFilters((prev) =>
			activeFilterKey(prev) === activeFilterKey(filters) ? prev : filters
		);
	};

	const columns: ColumnsType<SalesOrder> = [
		{
			title: "Order ID",
			dataIndex: "sales_order_id",
			key: "sales_order_id",
			sorter: true,
			sortOrder: sortField === "sales_order_id" ? (sortDirection === "asc" ? "ascend" : "descend") : undefined,
			...getColumnSearchProps("sales_order_id", "Search Order ID"),
			render: (id: number, record: SalesOrder) => (
				<Space direction="vertical" size={0}>
					<Text strong style={{ fontFamily: "monospace" }}>
						{record.is_shopify_order ? `#${record.shopify_order_number}` : `SO-${String(id).padStart(4, "0")}`}
					</Text>
					{record.is_shopify_order && (
						<StatusTag status="shopify" label="Shopify" />
					)}
				</Space>
			),
			width: 140,
		},
		{
			title: "Customer",
			dataIndex: "shopify_customer_name",
			key: "customer_name",
			sorter: true,
			sortOrder: sortField === "shopify_customer_name" ? (sortDirection === "asc" ? "ascend" : "descend") : undefined,
			...getColumnSearchProps("customer_name", "Search Customer"),
			render: (_, record: SalesOrder) => {
				if (record.is_shopify_order) {
					return (
						<Space direction="vertical" size={0}>
							<Text>{record.shopify_customer_name || "—"}</Text>
							<Text type="secondary" style={{ fontSize: 11 }}>
								{record.shopify_customer_email}
							</Text>
						</Space>
					);
				}
				const assigneeId = record.assignee_id ? String(record.assignee_id) : "";
				if (!assigneeId) return <Text type="secondary">—</Text>;

				const patient = assignees.get(assigneeId.toLowerCase());
				if (patient) {
					const label = patientLabel(patient);
					return (
						<Space direction="vertical" size={0}>
							<Link
								onClick={(e) => {
									// The row click would also open the order drawer.
									e.stopPropagation();
									openPatient(assigneeId);
								}}
							>
								{label}
							</Link>
							{patient.email && patient.email !== label && (
								<Text type="secondary" style={{ fontSize: 11 }}>
									{patient.email}
								</Text>
							)}
						</Space>
					);
				}
				return (
					<Space direction="vertical" size={0}>
						{!assigneesLoading && <Text>Unknown patient</Text>}
						<Text type="secondary" style={{ fontSize: 11, maxWidth: 200 }}>
							{assigneeId}
						</Text>
					</Space>
				);
			},
		},
		{
			title: "Order Date",
			dataIndex: "order_date",
			key: "order_date",
			sorter: true,
			sortOrder: sortField === "order_date" ? (sortDirection === "asc" ? "ascend" : "descend") : undefined,
			filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
				<div style={{ padding: 8 }}>
					<DatePicker.RangePicker
						value={selectedKeys[0] ? [dayjs(selectedKeys[0]), dayjs(selectedKeys[1])] : null}
						onChange={(dates) => {
							if (dates && dates[0] && dates[1]) {
								setSelectedKeys([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]);
							} else {
								setSelectedKeys([]);
							}
						}}
						style={{ marginBottom: 8, display: 'block' }}
					/>
					<Space>
						<Button type="primary" onClick={() => confirm()} size="small">
							Filter
						</Button>
						<Button onClick={() => clearFilters()} size="small">
							Reset
						</Button>
					</Space>
				</div>
			),
			filterIcon: (filtered: boolean) => (
				<SearchOutlined style={{ color: filtered ? 'var(--mg-primary)' : undefined }} />
			),
			render: (date: string) => (date ? new Date(date).toLocaleDateString() : "—"),
			width: 130,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			sorter: true,
			sortOrder: sortField === "status" ? (sortDirection === "asc" ? "ascend" : "descend") : undefined,
			filters: [
				{ text: 'Pending', value: 'pending' },
				{ text: 'Processing', value: 'processing' },
				{ text: 'Completed', value: 'completed' },
				{ text: 'Cancelled', value: 'cancelled' },
			],
			filterMultiple: false,
			render: (_, record: SalesOrder) => {
				if (record.is_shopify_order) {
					return (
						<Space wrap size={2}>
							<StatusTag status={record.status} />
							{record.shopify_financial_status && (
								<StatusTag status={record.shopify_financial_status} label={record.shopify_financial_status} />
							)}
							{record.shopify_fulfillment_status && (
								<StatusTag status={record.shopify_fulfillment_status} label={record.shopify_fulfillment_status} />
							)}
						</Space>
					);
				}
				return <StatusTag status={record.status} />;
			},
		},
		{
			title: "Type",
			key: "is_shopify_order",
			filters: [
				{ text: 'Shopify Orders', value: 'true' },
				{ text: 'Manual Orders', value: 'false' },
			],
			filterMultiple: false,
			render: (_, record: SalesOrder) => (
				<StatusTag
					status={record.is_shopify_order ? "active" : "assigned"}
					label={record.is_shopify_order ? "Shopify" : "Manual"}
				/>
			),
			width: 90,
		},
		{
			title: "Labels",
			key: "labels",
			width: 160,
			render: (_, record: SalesOrder) => {
				const ids = (record.labels || [])
					.map((l) => l.stock_label?.label_id)
					.filter(Boolean) as string[];

				if (ids.length === 0) return <Text type="secondary">—</Text>;

				return (
					<Tooltip title={ids.length > 1 ? ids.join(", ") : undefined}>
						<Space direction="vertical" size={0}>
							<Text style={{ fontFamily: "monospace", fontSize: 12 }}>{ids[0]}</Text>
							{ids.length > 1 && (
								<Text type="secondary" style={{ fontSize: 11 }}>+{ids.length - 1} more</Text>
							)}
						</Space>
					</Tooltip>
				);
			},
		},
		{
			title: "Total",
			dataIndex: "total_amount",
			key: "total_amount",
			sorter: true,
			sortOrder: sortField === "total_amount" ? (sortDirection === "asc" ? "ascend" : "descend") : undefined,
			align: "right",
			render: (value: number | string) => {
				const numValue = typeof value === "string" ? parseFloat(value) : value;
				return numValue ? (
					<Text strong style={{ fontSize: 14 }}>
						${numValue.toFixed(2)}
					</Text>
				) : (
					"—"
				);
			},
			width: 130,
		},
	];

	// The term searched matched a real kit id but no order — say so, rather than
	// leaving the user to guess whether the label exists at all.
	let emptyTitle = "No sales orders found";
	let emptyDescription = "Create your first order or adjust your search filters";

	if (labelLookup) {
		emptyTitle = "No sales order for this label ID";
		emptyDescription =
			`${labelLookup.matches} label${labelLookup.matches === 1 ? "" : "s"} ` +
			`matching "${labelLookup.term}" exist${labelLookup.matches === 1 ? "s" : ""} in stock, ` +
			`but none is assigned to a sales order yet.`;
	}

	return (
		<>
			<PageToolbar
				primaryAction={{ label: "New Order", icon: <PlusOutlined />, onClick: handleNewOrder }}
				extraActions={[
					{ label: "Refresh", icon: <ReloadOutlined />, onClick: loadOrders },
				]}
				filters={[
					{
						type: "search",
						placeholder: "Global search across all fields…",
						value: globalSearchInput,
						onChange: (v) => {
							setGlobalSearchInput(v);
							debouncedSetGlobalSearch(v);
						},
						width: 320,
					},
					...(canViewPatients()
						? [
								{
									type: "custom" as const,
									key: "patient",
									render: (
										<PatientSelect
											value={patientFilter}
											onChange={(v) => setPatientFilter(v)}
											placeholder="Filter by patient"
											allowClear
											style={{ width: 240 }}
											data-testid="orders-patient-filter"
										/>
									),
								},
							]
						: []),
				]}
				onReset={clearAllFilters}
			/>

			<DataTable
				columns={columns}
				dataSource={orders}
				rowKey="sales_order_id"
				loading={loading}
				total={totalOrders}
				currentPage={currentPage}
				pageSize={ORDERS_PER_PAGE}
				onPageChange={(page) => setCurrentPage(page)}
				onChange={handleTableChange}
				onRowClick={handleEditOrder}
				emptyTitle={emptyTitle}
				emptyDescription={emptyDescription}
				fillHeight
			/>

			{selectedOrder && <EditSalesOrderDrawer isOpen={drawerOpen} onClose={handleDrawerClose} order={selectedOrder} />}
			{modals}
		</>
	);
};

export default SalesOrdersTab;
